import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import { TranslatableInputText } from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Toggle from '@/design_system/Toggle';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import {
  ConditionFormFromGet,
  useCreateConditionFormQuestion,
  useDeleteConditionFormAnswer,
  useUpdateConditionFormAnswer,
} from '@/models/conditionForm';
import { useDefectTypesOrganization } from '@/models/defectType';
import { ProductAttributeTrees } from '@/routes/SettingsBeta/components/ProductAttributeTrees/ProductAttributeTrees';
import { type LegacySettingsLocationState } from '@/routes/SettingsBeta/legacy-settings';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import { trimI18nWording } from '@/utils/trimI18nWording';

import { useSettingsScope } from '../../../../../../SettingsContext';

import {
  ProductAttributeValue,
  ProductAttributeValuesInput,
} from './components/ProductAttributeValuesInput';
import { type ConditionFormQuestion, ConditionQuestion } from './ConditionQuestion';

const { element } = createBEMClasses('settings__condition-form__question');

type ConditionFormAnswer = NonNullable<ConditionFormQuestion['answers']>[number];

export const ConditionAnswer = ({
  form,
  question,
  answer,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
  answer: ConditionFormAnswer;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <ConditionAnswerEditMode
        form={form}
        question={question}
        answer={answer}
        onClose={() => setIsEditing(false)}
      />
    );
  }

  return (
    <ConditionAnswerReadOnly
      form={form}
      answer={answer}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onEdit={() => {
        setIsEditing(true);
        setIsOpen(true);
      }}
    />
  );
};

const ConditionAnswerReadOnly = ({
  form,
  answer,
  onEdit,
  isOpen,
  setIsOpen,
}: {
  form: ConditionFormFromGet;
  answer: ConditionFormAnswer;
  onEdit: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const showToast = useShowToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutateAsync: deleteConditionFormAnswer, isPending: isDeleteConditionFormAnswerPending } =
    useDeleteConditionFormAnswer();

  const answerText = answer.text ? _db(answer.text) : '';

  const handleConfirmDelete = async () => {
    try {
      await deleteConditionFormAnswer({
        conditionFormId: form.id,
        answerId: answer.id,
      });
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.success">
            &quot;{answerText}&quot; has been deleted.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.error">
            Could not delete &quot;{answerText}&quot;. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Delete failed');
    }
  };

  return (
    <>
      <div className={element('answer', { isOpen })}>
        <div className={element('answer__header-row')}>
          <Button
            variant="style-less"
            className={element('answer__trigger')}
            onPress={() => setIsOpen(!isOpen)}
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.toggle',
              message: `Toggle answer ${answerText}`,
            })}
          >
            <Stack
              row
              alignItems="center"
              gap="0.5rem"
              flexWrap="nowrap"
              className={element('answer__header')}
            >
              <IconChevron down={!isOpen} up={isOpen} />
              <Paragraph className={element('answer__title')}>{answerText}</Paragraph>
              {'subQuestion' in answer && answer.subQuestion && (
                <Badge size="x-small" color="neutral" hasBorder>
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.has-sub-question">
                    Has sub-question
                  </Trans>
                </Badge>
              )}
              {answer.defects.length > 0 && (
                <Badge size="x-small" color="neutral" hasBorder>
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.has-defect">
                    Has associated defect
                  </Trans>
                </Badge>
              )}
              {form.type === 'quality_control' && answer.makesQualityControlKO && (
                <Badge size="x-small" color="danger" hasBorder>
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.makes-quality-control-ko.badge">
                    Makes quality control KO
                  </Trans>
                </Badge>
              )}
            </Stack>
          </Button>

          <Button
            variant="style-less"
            className={element('answer__header__button', { edit: true })}
            iconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.edit.aria',
              message: `Edit answer ${answerText}`,
            })}
            onPress={() => onEdit()}
          >
            <IconEdit />
          </Button>

          <Button
            variant="style-less"
            className={element('answer__header__button', { delete: true })}
            iconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.aria',
              message: `Delete answer ${answerText}`,
            })}
            onPress={() => setShowDeleteConfirmation(true)}
          >
            <IconTrash />
          </Button>
        </div>

        {isOpen && (
          <div className={element('answer__content')}>
            <Stack gap="0.5rem">
              <Paragraph>
                <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.product-attribute-values">
                  Product attributes:
                </Trans>
                {answer.productAttributeValues.length === 0 && (
                  <InlineText weight="medium">
                    {' '}
                    <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.product-attribute-values.all">
                      All
                    </Trans>
                  </InlineText>
                )}
              </Paragraph>
              {answer.productAttributeValues.length > 0 && (
                <div style={{ paddingLeft: '1rem' }}>
                  <ProductAttributeTrees productAttributeValues={answer.productAttributeValues} />
                </div>
              )}
            </Stack>

            {form.type === 'initial_condition' &&
              (!('subQuestion' in answer) || !answer.subQuestion) && (
                <Stack row gap="0.25rem">
                  <Paragraph>
                    <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.defects">
                      Associated defect:
                    </Trans>
                    {answer.defects.length === 0 && (
                      <InlineText weight="medium">
                        {' '}
                        <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.defects.none">
                          None
                        </Trans>
                      </InlineText>
                    )}
                  </Paragraph>
                  {answer.defects.length > 0 && (
                    <Stack row gap="0.25rem">
                      {answer.defects.map((defect, index) => (
                        <span key={defect.id}>
                          <Button
                            variant="link-subtle"
                            onPress={() =>
                              navigate('/settings?tab=defect-types', {
                                state: {
                                  fromSettingsBeta: true,
                                  backTo: pathname,
                                  defectTypeId: defect.id,
                                } satisfies LegacySettingsLocationState,
                              })
                            }
                          >
                            <InlineText weight="medium">{_db(defect.name)}</InlineText>
                          </Button>
                          {index < answer.defects.length - 1 && ', '}
                        </span>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}

            {form.type === 'quality_control' ? (
              answer.makesQualityControlKO ? (
                <Paragraph>
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.makes-quality-control-ko.yes">
                    This answer will make the quality control KO
                  </Trans>
                </Paragraph>
              ) : (
                <Paragraph>
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.makes-quality-control-ko.no">
                    This answer will <InlineText weight="heavy">not</InlineText> make the quality
                    control KO
                  </Trans>
                </Paragraph>
              )
            ) : null}

            {'subQuestion' in answer && answer.subQuestion && (
              <Stack gap="0.5rem">
                <Paragraph weight="medium">
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.sub-question">
                    Sub-question:
                  </Trans>
                </Paragraph>
                <ConditionQuestion form={form} question={answer.subQuestion} />
              </Stack>
            )}
          </div>
        )}
      </div>

      <ConfirmationDialog
        title={
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.title">
            Delete answer
          </Trans>
        }
        confirm={
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.confirm">
            Delete
          </Trans>
        }
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        isPending={isDeleteConditionFormAnswerPending}
        onConfirm={handleConfirmDelete}
        maxWidth="35rem"
      >
        <Paragraph>
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.delete.description">
            Are you sure you want to delete &quot;{answerText}&quot; and any associated
            sub-question?
          </Trans>
        </Paragraph>
      </ConfirmationDialog>
    </>
  );
};

const ConditionAnswerEditMode = ({
  form,
  question,
  answer,
  onClose,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
  answer: ConditionFormAnswer;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const showToast = useShowToast();
  const { mutateAsync: updateConditionFormAnswer, isPending: isUpdateConditionFormAnswerPending } =
    useUpdateConditionFormAnswer();
  const {
    mutateAsync: createConditionFormQuestion,
    isPending: isCreateConditionFormQuestionPending,
  } = useCreateConditionFormQuestion();
  const { scope } = useSettingsScope();

  const [text, setText] = useState(answer.text ?? { en: '', fr: '' });
  const [productAttributeValues, setProductAttributeValues] = useState<ProductAttributeValue[]>(
    answer.productAttributeValues
  );
  const [associatedDefects, setAssociatedDefects] = useState(answer.defects);
  const [makesQualityControlKO, setMakesQualityControlKO] = useState(
    answer.makesQualityControlKO ?? false
  );

  const textLocalized = answer.text ? _db(answer.text) : '';

  const handleSave = async () => {
    try {
      await updateConditionFormAnswer({
        conditionFormId: form.id,
        answerId: answer.id,
        text: trimI18nWording(text),
        productAttributeValues: productAttributeValues.map(({ id }) => ({ id })),
        defects: associatedDefects.map(({ id }) => ({ id })),
        makesQualityControlKO,
      });
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.save.success">
            &quot;{_db(text)}&quot; has been saved.
          </Trans>
        ),
        type: 'success',
      });
      onClose();
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.save.error">
            Could not save &quot;{_db(text)}&quot;. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Save failed');
    }
  };

  const handleAddSubQuestion = async () => {
    try {
      await createConditionFormQuestion({
        conditionFormId: form.id,
        parentAnswerId: answer.id,
      });
      onClose();
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.add-sub-question.error">
            Could not add a sub-question. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Add sub-question failed');
    }
  };

  return (
    <div className={element('answer', { isOpen: true, isEditing: true })}>
      <div className={element('answer__header-row')}>
        <Stack
          row
          alignItems="center"
          gap="0.5rem"
          flexWrap="nowrap"
          className={element('answer__header')}
        >
          <TranslatableInputText
            size="small"
            value={text}
            onChange={setText}
            className={element('answer__header__input')}
            aria-label={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.text.aria',
              message: `Answer text for ${textLocalized}`,
            })}
            placeholder={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.text.placeholder',
              message: 'Text of the answer',
            })}
          />
        </Stack>

        <Button
          variant="style-less"
          className={element('answer__header__button', { save: true })}
          iconOnly
          isLoading={isUpdateConditionFormAnswerPending}
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.save.aria',
            message: `Save answer ${textLocalized}`,
          })}
          onPress={handleSave}
        >
          <IconCheck />
        </Button>
        <Button
          variant="style-less"
          className={element('answer__header__button', { cancel: true })}
          iconOnly
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.cancel.aria',
            message: `Cancel editing answer ${textLocalized}`,
          })}
          onPress={() => onClose()}
        >
          <IconCross />
        </Button>
      </div>
      <div className={element('answer__content')}>
        <ProductAttributeValuesInput
          organizationId={scope!.organizationId}
          selectedProductAttributeValues={productAttributeValues}
          setSelectedProductAttributeValues={setProductAttributeValues}
        />

        {!('subQuestion' in answer && answer.subQuestion) && (
          <Stack gap="0.5rem">
            {form.type === 'initial_condition' && (
              <AssociatedDefectSearchSelect
                defects={associatedDefects}
                onChange={setAssociatedDefects}
              />
            )}

            {form.type === 'quality_control' && (
              <Toggle
                label={t({
                  id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.makes-quality-control-ko.label',
                  message: 'Makes quality control KO',
                })}
                isSelected={makesQualityControlKO}
                onChange={setMakesQualityControlKO}
                alignment="left"
              />
            )}

            {form.questions.find((q) => q.id === question.id) && (
              <div>
                <Button
                  variant="link-subtle"
                  size="small"
                  isLoading={isCreateConditionFormQuestionPending}
                  onPress={handleAddSubQuestion}
                >
                  <IconAdd />
                  <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.answer.add-sub-question">
                    Add sub-question
                  </Trans>
                </Button>
              </div>
            )}
          </Stack>
        )}

        {'subQuestion' in answer && answer.subQuestion && (
          <Stack gap="0.5rem">
            <Paragraph weight="medium">
              <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.sub-question">
                Sub-question:
              </Trans>
            </Paragraph>
            <ConditionQuestion form={form} question={answer.subQuestion} />
          </Stack>
        )}
      </div>
    </div>
  );
};

const AssociatedDefectSearchSelect = ({
  defects,
  onChange,
}: {
  defects: { id: string; name: I18nWording }[];
  onChange: (defects: { id: string; name: I18nWording }[]) => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const { data: defectTypes = [], isLoading } = useDefectTypesOrganization();

  return (
    <InputSearchSelect
      isMulti
      size="small"
      isLoading={isLoading}
      label={t({
        id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.associated-defects.label',
        message: 'Associated defects',
      })}
      placeholder={t({
        id: 'settings.organization.data-catalogs.condition-forms.detail.question.answer.associated-defects.placeholder',
        message: 'Select associated defects',
      })}
      value={defects}
      options={defectTypes}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => (option ? _db(option.name) : '')}
      onChange={(options) => onChange([...options])}
      onRemoveValue={(id) => {
        onChange(defects.filter((defect) => defect.id !== id));
      }}
    />
  );
};
